import {inject, ChangeDetectionStrategy, Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Store, select} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {BehaviorSubject, Observable} from 'rxjs';
import {colors} from 'src/app/app.constants';
import {Project} from 'src/app/core/model/project';
import {RewardCoin} from 'src/app/core/model/reward';
import {SkillModel} from 'src/app/core/model/skill';
import {User} from 'src/app/core/model/user';
import {GetLocationsAction} from 'src/app/core/store/locations/locations.action';
import {selectAllLocations} from 'src/app/core/store/locations/locations.selector';
import {CreateProjectAction, GetProjectsAction} from 'src/app/core/store/projects/projects.action';
import {selectAllProjects} from 'src/app/core/store/projects/projects.selector';
import {selectSkillsList} from 'src/app/core/store/skills/skills.selector';
import {GetTeamsAction} from 'src/app/core/store/teams/teams.action';
import {selectAllTeams} from 'src/app/core/store/teams/teams.selector';
import {GetAllUsersAction} from 'src/app/core/store/users/users.action';
import {selectAllUsers} from 'src/app/core/store/users/users.selector';
import {Location} from 'src/app/locations/shared/model/location';
import {EMPTY_PROJECT_STEP} from 'src/app/projects/shared/projects-constants';
import {MessageService} from 'src/app/services/message.service';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphColorPickerComponent} from 'src/app/shared/design/oph-color-picker/oph-color-picker.component';
import {SuccessDialogComponent} from 'src/app/shared/dialog/success-dialog/success-dialog.component';
import {Team} from 'src/app/teams/shared/model/team';
import {ProjectsTaskDialogComponent} from '../../tasks/projects-task-dialog/projects-task-dialog.component';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'create-project-dialog',
  templateUrl: './create-project-dialog.component.html',
  styleUrls: ['./create-project-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateProjectDialogComponent implements OnInit {
  private readonly mobileService = inject(MobileService);
  public readonly form: FormGroup = new FormGroup({
    label: new FormControl(null, Validators.required),
  });

  currentSkedIndex$ = new BehaviorSubject<number>(null);
  locations$: Observable<Location[]>;
  locationsMap$: Observable<Record<string, Location>>;
  projects$: Observable<Project[]>;
  requirements$: Observable<SkillModel[]>;
  teams$: Observable<Team[]>;
  users$: Observable<User[]>;

  public readonly colors: string[] = colors;
  public showPicker: boolean = false;
  public selectedColor$: BehaviorSubject<string> = new BehaviorSubject<string>('#C7C2B6');

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<CreateProjectDialogComponent>,
    public dialogSuccessRef: MatDialogRef<SuccessDialogComponent>,
    public colorPickerRef: MatDialogRef<OphColorPickerComponent>,
    public store$: Store,
    public messageService: MessageService,
    private timezoneService: TimezoneService,
    @Inject(MAT_DIALOG_DATA) public data: {onFinishedNewTask: () => void; coinTypes: RewardCoin[]}
  ) {
    this.store$.dispatch(new GetLocationsAction({}));
    this.store$.dispatch(new GetTeamsAction({}));
    this.store$.dispatch(new GetAllUsersAction({}));
    this.requirements$ = this.store$.pipe(select(selectSkillsList));
    this.locations$ = this.store$.pipe(select(selectAllLocations));
    this.teams$ = this.store$.pipe(select(selectAllTeams));
    this.users$ = this.store$.pipe(select(selectAllUsers));
  }

  ngOnInit(): void {
    this.projects$ = this.store$.pipe(select(selectAllProjects(true)));
    this.store$.dispatch(new GetLocationsAction({}));
    this.store$.dispatch(new GetProjectsAction({}));

    this.locations$ = this.store$.pipe(select(selectAllLocations));
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onColorSelected(color: string): void {
    this.selectedColor$.next(color);
  }

  onCancel(): void {
    this.colorPickerRef.close();
  }

  openSuccessDialog(): void {
    this.dialogSuccessRef = this.dialog.open(SuccessDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: 'Task added successfully',
      panelClass: 'full-dialog',
    });
    setTimeout(() => {
      this.dialogSuccessRef.close();
    }, 2000);
  }

  onNext(): void {
    const project: Project = {
      label: this.form.value.label,
      color: this.selectedColor$.getValue(),
      startDateTime: moment().tz(this.timezoneService.getTimeZone()).startOf('day').subtract(1, 'days').format(),
      endDateTime: moment().tz(this.timezoneService.getTimeZone()).startOf('day').add(6, 'months').format(),
      status: 'active',
      program: [],
      steps: [EMPTY_PROJECT_STEP],
    };

    this.store$.dispatch(
      new CreateProjectAction({
        project,
        onSuccess: p => this.onSaveProjectSuccess(p),
      })
    );
  }

  onSaveProjectSuccess(project: Project) {
    this.dialogRef.close();
    this.openTaskDialog(project);
  }

  openTaskDialog(project: Project) {
    const dialogRef = this.dialog.open(ProjectsTaskDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: {
        project,
        fromCurrentSked: true,
        coinTypes: this.data.coinTypes,
        stepId: project.steps[0]._id,
        taskIndex: 0,
        newTask: true,
      },
      panelClass: 'task-dialog',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((manualEntry: boolean) => {
      if (this.data.onFinishedNewTask) {
        this.data.onFinishedNewTask();
      }
    });
  }

  togglePicker(): void {
    this.showPicker = !this.showPicker;
  }
}
